import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../interceptors";
import EmptyState from "../../components/shared/EmptyState";
import LoadingState from "../../components/shared/LoadingState";
import ErrorState from "../../components/shared/ErrorState";
import { toast } from "../../components/shared/toast";

// ── /candidate/notifications ───────────────────────────────────────────────
// Backend:
//   POST /job-posting/api/candidate-portal/notifications/list  → all rows
//   POST /job-posting/api/candidate-portal/notifications/mark-read
//
// Rows come from amplehire_candidate_notification_t (master-only —
// MasterDataSourceFilter already routes /api/candidate-portal/** to
// master, so the calls work for any tenant).

const TYPE_ICON = {
  INTERVIEW_INVITE: "🎥",
  REMINDER:         "⏰",
  STATUS_CHANGE:    "📌",
  SCORE_READY:      "📊",
  OFFER:            "✉️",
};

function fmtRel(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function CandidateNotifications() {
  const candidateId = useMemo(() => {
    const raw = localStorage.getItem("candidateId") || localStorage.getItem("userId");
    return raw ? Number(raw) : null;
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  const load = useCallback(() => {
    if (!candidateId) {
      setLoading(false);
      setError("Not signed in as a candidate");
      return;
    }
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    axiosInstance
      .post("/job-posting/api/candidate-portal/notifications/list", { candidateId })
      .then((r) => {
        if (r.data?.success) {
          setRows(Array.isArray(r.data.data) ? r.data.data : []);
        } else {
          setError(r.data?.message || "Couldn't load notifications");
          setErrorStatus(500);
        }
      })
      .catch((e) => {
        setError(e?.message || "Couldn't load notifications");
        setErrorStatus(e?.status || e?.response?.status || 500);
      })
      .finally(() => setLoading(false));
  }, [candidateId]);

  useEffect(() => {
    document.title = "Notifications - Ample Hire";
    load();
  }, [load]);

  const unreadCount = useMemo(() => rows.filter((n) => !n.isRead).length, [rows]);

  const markRead = async (notificationId) => {
    // Optimistic: flip locally first, roll back on error.
    setRows((prev) => prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n)));
    try {
      await axiosInstance.post("/job-posting/api/candidate-portal/notifications/mark-read", {
        notificationId,
      });
    } catch (e) {
      // Rollback + tell the user.
      setRows((prev) => prev.map((n) => (n.id === notificationId ? { ...n, isRead: false } : n)));
      toast.error("Couldn't mark as read", { description: e?.message });
    }
  };

  const markAllRead = async () => {
    const unread = rows.filter((n) => !n.isRead);
    if (unread.length === 0) return;
    // Optimistic flip everyone.
    setRows((prev) => prev.map((n) => ({ ...n, isRead: true })));
    let failed = 0;
    for (const n of unread) {
      try {
        await axiosInstance.post("/job-posting/api/candidate-portal/notifications/mark-read", {
          notificationId: n.id,
        });
      } catch {
        failed += 1;
      }
    }
    if (failed === 0) {
      toast.success(`Marked ${unread.length} notification${unread.length === 1 ? "" : "s"} as read`);
    } else {
      toast.warning(`${unread.length - failed} marked; ${failed} failed`);
      // Best-effort refresh so the bad ones flip back.
      load();
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base m-0">Notifications</h1>
          <p className="text-sm text-vf-muted mt-1">
            Invites, reminders, and status updates from your hiring process.
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="text-xs px-3 py-1.5 rounded border border-subtle bg-white text-vf-base hover:bg-vf-tint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
          >
            Mark all as read ({unreadCount})
          </button>
        )}
      </header>

      {loading ? (
        <div className="bg-white border border-subtle rounded-card p-4">
          <LoadingState variant="skeleton" shape="row" count={4} />
        </div>
      ) : error ? (
        <ErrorState
          status={errorStatus}
          description={errorStatus === 403 ? undefined : error}
          retry={load}
        />
      ) : rows.length === 0 ? (
        <div className="bg-white border border-subtle rounded-card">
          <EmptyState
            icon={<span aria-hidden="true">🔔</span>}
            title="No notifications yet"
            description="You'll get a notification when a recruiter sends you an invite or your results are ready."
          />
        </div>
      ) : (
        <ul className="list-none p-0 m-0 space-y-2">
          {rows.map((n) => {
            const icon = TYPE_ICON[n.notificationType] || "🔔";
            const unread = !n.isRead;
            const linkable = !!n.linkUrl;
            const Wrapper = linkable ? "a" : "div";
            const wrapperProps = linkable ? { href: n.linkUrl } : {};
            return (
              <li key={n.id}>
                <Wrapper
                  {...wrapperProps}
                  className={`block rounded-card border px-4 py-3 transition-colors ${
                    unread
                      ? "border-vf-primary/30 bg-vf-tint/40 hover:bg-vf-tint"
                      : "border-subtle bg-white hover:border-vf-primary/30"
                  } ${linkable ? "cursor-pointer" : ""}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-lg flex-shrink-0 mt-0.5" aria-hidden="true">{icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <div className="text-sm font-semibold text-vf-base flex-1 min-w-0">
                          {n.title || "Notification"}
                        </div>
                        {unread && (
                          <span
                            className="w-2 h-2 rounded-full bg-vf-primary flex-shrink-0 mt-1.5"
                            aria-label="Unread"
                          />
                        )}
                      </div>
                      {n.message && (
                        <p className="text-xs text-vf-muted mt-1 m-0 leading-relaxed">{n.message}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <time className="text-2xs text-vf-muted" dateTime={n.createdAt || undefined}>
                          {fmtRel(n.createdAt)}
                        </time>
                        {unread && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); markRead(n.id); }}
                            className="text-2xs text-vf-primary hover:underline font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary rounded"
                          >
                            Mark as read
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </Wrapper>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
